import { useCallback, useEffect, useState } from "react";
import {
  applyEdgeChanges,
  Connection,
  Edge,
  EdgeAddChange,
  EdgeChange,
  EdgeRemoveChange,
  EdgeResetChange,
  MarkerType,
  OnConnect,
  OnEdgesChange,
  OnEdgesDelete,
} from "reactflow";
import * as Y from "yjs";

type OnEdgeClick = (event: React.MouseEvent, edge: Edge) => void;

const isEdgeAddChange = (change: EdgeChange): change is EdgeAddChange => change.type === "add";
const isEdgeResetChange = (change: EdgeChange): change is EdgeResetChange =>
  change.type === "reset";
const isEdgeRemoveChange = (change: EdgeChange): change is EdgeRemoveChange =>
  change.type === "remove";

function useEdgesStateSynced(
  edgesMap?: Y.Map<Edge>
): [Edge[], OnEdgesChange, OnConnect, OnEdgeClick] {
  const [edges, setEdges] = useState<Edge[]>([]);

  const onEdgesChange: OnEdgesChange = useCallback(
    (changes) => {
      if (!edgesMap) return;
      const currentEdges = Array.from(edgesMap.values()).filter((e) => e);
      const nextEdges = applyEdgeChanges(changes, currentEdges);
      changes.forEach((change: EdgeChange) => {
        if (isEdgeRemoveChange(change)) {
          edgesMap.delete(change.id);
        } else if (!isEdgeAddChange(change) && !isEdgeResetChange(change)) {
          edgesMap.set(change.id, nextEdges.find((n) => n.id === change.id) as Edge);
        }
      });
    },
    [edgesMap]
  );

  const onConnect = useCallback(
    (params: Connection | Edge) => {
      if (!edgesMap) return;
      const { source, sourceHandle, target, targetHandle } = params;
      const id = `edge-${source}${sourceHandle || ""}-${target}${targetHandle || ""}`;

      edgesMap.set(id, {
        id,
        ...params,
      } as Edge);
    },
    [edgesMap]
  );

  const onEdgeClick = useCallback(
    (event: React.MouseEvent, eventEdge: Edge) => {
      if (!edgesMap) return;
      event.preventDefault();
      const currentEdges = Array.from(edgesMap.values()).filter((e) => e);
      const edge = currentEdges.find((n) => n.id === eventEdge.id);

      const marker: any = {};

      if (edge.markerStart) {
        marker.markerEnd = {
          type: MarkerType.Arrow,
          width: 40,
          height: 40,
        };
        marker.markerStart = undefined;
      }

      if (edge.markerEnd) {
        marker.markerEnd = undefined;
        marker.markerStart = undefined;
      }

      if (!edge.markerEnd && !edge.markerStart) {
        marker.markerStart = {
          type: MarkerType.Arrow,
          width: 40,
          height: 40,
        };
      }
      edgesMap.set(edge.id, {
        ...edge,
        ...marker,
      } as Edge);
    },
    [edgesMap]
  );

  useEffect(() => {
    if (!edgesMap) return;
    const observer = () => {
      setTimeout(() => {
        setEdges(Array.from(edgesMap.values()));
      }, 1);
    };

    setTimeout(() => {
      setEdges(Array.from(edgesMap.values()));
    }, 1);
    edgesMap.observe(observer);

    return () => edgesMap.unobserve(observer);
  }, [edgesMap, setEdges]);

  return [edges, onEdgesChange, onConnect, onEdgeClick];
}

export default useEdgesStateSynced;
